import React, { FormEventHandler, useMemo, useState } from 'react';
import { loadStripe, Stripe, StripeElementsOptions, StripeElementsOptionsMode } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { cn } from '@utils/index';
import { createServerSetupIntent, initializePayment } from 'src/services/profile';
import { useAuthStore } from 'src/store/auth';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import { useFormControls } from '@features/onboarding/hooks/useFormControls';
import { usePaymentStore } from '../../store/payment';
import { AnimatedDots } from '@components/shared/animated-dots/AnimatedDots';
import { useApplicationStore } from 'src/store/application';

// Publishable key from environment variables
const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_APP_STRIPE_PUBLISHABLE_KEY || '';
const stripePromise: Promise<Stripe | null> = loadStripe(STRIPE_PUBLISHABLE_KEY);

export const StripeBanksElementForm: React.FC<{ setCurrentStep: (step: 'connect' | 'submit') => void }> = ({ setCurrentStep }) => {
  const { user } = useAuthStore();
  const { currentApplicationId } = useApplicationStore();
  const [error, setError] = useState<string | null>(null);

  const { setIsLocalLoading } = useFormControls();

  console.log('user', user)

  const updatedFormData = useOnboardingStore.getState().data;
  const primaryApplicant = updatedFormData.applicants?.find((e) => e.applicant === 'primary');
  const [loading, setLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [serverSetupId, setServerSetupId] = useState<string | null>(null);
  const applicantFullName = useMemo(() => {
    const first = (primaryApplicant as any)?.firstName || '';
    const last = (primaryApplicant as any)?.lastName || '';
    return [first, last].filter(Boolean).join(' ').trim() || undefined;
  }, [primaryApplicant]);

  if (!STRIPE_PUBLISHABLE_KEY) {
    return <p className="text-error">Stripe publishable key is missing.</p>;
  }

  // Initialize SetupIntent on mount (server returns { id, secret })
  React.useEffect(() => {
    let mounted = true;
    // Try reuse from sessionStorage to avoid duplicate creations (StrictMode, remounts, toggles)

    if (!clientSecret && currentApplicationId) {
      setIsLocalLoading(true);
      // createServerSetupIntent()
      initializePayment(currentApplicationId)
        .then(({ id, secret }) => {
          if (!mounted) return;
          setServerSetupId(id);
          setClientSecret(secret);
        })
        .catch((e) => setError(e?.message || 'Failed to initialize payment'))
        .finally(() => {
          setIsLocalLoading(false);
        });
    }
    return () => {
      mounted = false;
    };
  }, []);

  const appearance = useMemo(() => ({ theme: 'stripe' as const }), []);

  const options: StripeElementsOptions | StripeElementsOptionsMode = clientSecret
    ? ({ clientSecret, appearance, loader: 'auto' } as StripeElementsOptions)
    : ({
      mode: 'payment',
      currency: 'usd',
      amount: 1000,
      paymentMethodTypes: ['us_bank_account'],
      setupFutureUsage: 'off_session',
      captureMethod: 'automatic',
      appearance,
    } as StripeElementsOptionsMode);

  if (!clientSecret) {
    return <p className="text-sm text-muted-foreground">
      Setting up secure payment…<br />
      <AnimatedDots />
    </p>;
  }

  return (
    <Elements stripe={stripePromise} options={options}>
      <PaymentElementInner
        setError={setError}
        setLoading={setLoading}
        loading={loading}
        userEmail={user?.email}
        userName={applicantFullName}
        clientSecret={clientSecret}
        serverSetupId={serverSetupId}
        setCurrentStep={setCurrentStep}
      />
    </Elements>
  );
};

const PaymentElementInner: React.FC<{
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  userEmail?: string;
  userName?: string;
  loading?: boolean;
  clientSecret?: string | null;
  serverSetupId?: string | null;
  setCurrentStep: (step: 'connect' | 'submit') => void;
}
> = ({ setError, setLoading, userEmail, userName, loading, clientSecret, serverSetupId, setCurrentStep }) => {
  const { setPaymentMethodId } = usePaymentStore();
  const stripe = useStripe();
  const elements = useElements();
  const [message, setMessage] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isPaymentConfirmed, setIsPaymentConfirmed] = useState(false);

  const { isLocalLoading } = useFormControls();
  const handleSubmit = async (e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault();

    if (!stripe || !elements || !clientSecret) {
      setMessage('Stripe is not initialized');
      return;
    }

    setMessage(null);
    setLoading(true);

    try {
      const billingDetails: { name?: string; email?: string } = {};
      if (userName) billingDetails.name = userName;
      if (userEmail) billingDetails.email = userEmail;

      // 1. Сначала валидируем форму через elements.submit()
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setMessage(submitError.message ?? 'Payment form validation failed');
        setError(submitError.message ?? 'Payment form validation failed');
        return;
      }

      // 2. Затем подтверждаем SetupIntent
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        clientSecret,
        confirmParams: {
          return_url: window.location.href,
          payment_method_data: {
            billing_details: billingDetails,
          },
        },
        redirect: 'if_required',
      });

      if (error || !setupIntent) {
        setMessage(error?.message ?? 'Payment setup failed');
        setError(error?.message ?? 'Payment setup failed');
        return;
      }

      const paymentMethodId = (setupIntent.payment_method || '') as string;
      if (!paymentMethodId) {
        setMessage('Payment method was not returned');
        setError('Payment method was not returned');
        return;
      }

      setMessage('Payment method saved successfully!');
      setIsSuccess(true);
      setPaymentMethodId(paymentMethodId);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      setMessage(errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form id="stripeForm" onSubmit={handleSubmit} className="space-y-4 max-w-md w-full mx-auto">
      <PaymentElement
        onChange={(event) => {
          console.log('event', event)
          if (event.complete) {
            setIsPaymentConfirmed(true);
            setCurrentStep('submit');
          }
        }}
        id="payment-element"
        options={{
          layout: { type: 'accordion', defaultCollapsed: false },
          paymentMethodOrder: ['us_bank_account'],
          fields: {
            billingDetails: {
              email: 'never',
              name: 'never',
            },
          },
          defaultValues: userEmail || userName ? { billingDetails: { email: userEmail, name: userName } } : undefined,
        }}
      />
      {message && message !== 'No account connected' && !isSuccess && (
        <p className={cn('text-sm', isSuccess ? 'text-green-500' : 'text-red-500')}>
          {message}
        </p>
      )}

      {/* Показываем кнопку только если банк еще не подключен */}
      {isPaymentConfirmed && (
        <div className="w-fit m-auto max-w-2xl">
          <div className="mt-6 text-center">
            <button
              onClick={() => handleSubmit()}
              className={cn(
                "bg-primary hover:bg-primary/90 text-white font-semibold py-3 px-8 rounded-lg transition-colors duration-200",
                (loading || isSuccess ? "pointer-events-none" : "")
              )}
            >
              {!isSuccess && <span className={(loading ? "opacity-75" : "")}>
                {!loading ? 'SUBMIT' : 'Processing...'}
              </span>}
              {isSuccess && <AnimatedDots />}
            </button>
          </div>
        </div>
      )}
    </form>
  );
};
